import { startAuthentication } from '@simplewebauthn/browser';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  ApiError,
  getSession,
  getPasskeyLoginOptions,
  loginWithPassword,
  logout,
  verifyPasskeyLogin,
  type SessionUser,
} from '../lib/api';

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  loginPassword: (email: string, password: string) => Promise<{ shouldRegisterPasskey: boolean }>;
  loginPasskey: () => Promise<void>;
  doLogout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      const session = await getSession();
      setUser(session.user);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUser(null);
        return;
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await refreshSession();
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshSession]);

  const loginPassword = useCallback(async (email: string, password: string) => {
    const result = await loginWithPassword({ email, password });
    setUser(result.user);
    return { shouldRegisterPasskey: result.should_register_passkey };
  }, []);

  const loginPasskey = useCallback(async () => {
    const optionsResponse = await getPasskeyLoginOptions();
    const assertion = await startAuthentication({
      optionsJSON: optionsResponse.options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
    });

    const verifyResponse = await verifyPasskeyLogin({
      sessionToken: optionsResponse.sessionToken,
      response: assertion,
    });

    setUser(verifyResponse.user);
  }, []);

  const doLogout = useCallback(async () => {
    await logout();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      loginPassword,
      loginPasskey,
      doLogout,
      refreshSession,
    }),
    [doLogout, loading, loginPasskey, loginPassword, refreshSession, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return value;
}
